<section>
  <header>
    <h1>Modération</h1>
    <button (click)="reload(currentPage)">Rafraîchir</button>
  </header>

  <div class="tabs">
    <button
      [class.active]="activeTab === 'recipes'"
      (click)="setTab('recipes')"
      type="button"
    >
      Recettes
    </button>
    <button
      [class.active]="activeTab === 'inspirations'"
      (click)="setTab('inspirations')"
      type="button"
    >
      Inspirations
    </button>
    <button
      [class.active]="activeTab === 'reviews'"
      (click)="setTab('reviews')"
      type="button"
    >
      Messages d’avis
    </button>
  </div>

  <div class="toolbar">
    <input
      type="search"
      [value]="searchQuery"
      (input)="applySearch(($any($event.target).value || '').toString())"
      placeholder="Rechercher..."
    />
  </div>

  <p class="error" *ngIf="errorMessage">{{ errorMessage }}</p>

  <div class="state loading-state" *ngIf="loading">
    <span class="spinner" aria-hidden="true"></span>
    <span>Chargement…</span>
  </div>

  <div class="state" *ngIf="!loading && activeTab === 'recipes' && recipes.length === 0">
    Aucune recette à modérer.
  </div>

  <div class="grid" *ngIf="!loading && activeTab === 'recipes' && recipes.length > 0">
    <article *ngFor="let recipe of recipes">
      <div class="cover">
        <ng-container *ngIf="recipeCoverImageUrl(recipe) as coverUrl; else coverPlaceholder">
          <img [src]="coverUrl" [alt]="recipe.name" />
        </ng-container>
        <ng-template #coverPlaceholder>
          <div class="cover-placeholder">Aucune image</div>
        </ng-template>
      </div>

      <div class="owner-line">
        <span>Publié par:</span>
        <a *ngIf="recipe.ownerId != null; else ownerAsText" [routerLink]="['/dashboard/users', recipe.ownerId]">
          {{ recipeOwnerLabel(recipe) }}
        </a>
        <ng-template #ownerAsText>
          <span class="owner-text">{{ recipeOwnerLabel(recipe) }}</span>
        </ng-template>
      </div>

      <h2>{{ recipe.name }}</h2>
      <p class="recipe-description" [class.expanded]="isRecipeExpanded(recipe.id)">
        {{ recipe.description || 'Sans description' }}
      </p>

      <div class="actions">
        <button
          class="ghost"
          [disabled]="isActionPending('recipe:' + recipe.id)"
          (click)="toggleRecipeDetails(recipe.id)"
        >
          {{ isRecipeExpanded(recipe.id) ? 'Masquer le contenu' : 'Voir le contenu' }}
        </button>

        <button
          class="ok"
          [disabled]="isActionPending('recipe:' + recipe.id)"
          (click)="decideRecipe(recipe, 'APPROVED')"
        >
          Approuver
        </button>
        <button
          class="ko"
          [disabled]="isActionPending('recipe:' + recipe.id)"
          (click)="openRejectDialog(recipe)"
        >
          Rejeter
        </button>
      </div>

      <section class="details" *ngIf="isRecipeExpanded(recipe.id)">
        <ng-container *ngIf="hasRecipeDetails(recipe); else noDetails">
          <div class="details-meta">
            <span *ngIf="recipe.difficulty">Difficulté: {{ recipe.difficulty }}</span>
            <span *ngIf="recipe.cost">Coût: {{ recipe.cost }}</span>
            <span *ngIf="recipe.cookingTime != null">Cuisson: {{ recipe.cookingTime }} min</span>
            <span *ngIf="recipe.numberOfPersons != null">Personnes: {{ recipe.numberOfPersons }}</span>
            <span *ngIf="recipeCategoryLabel(recipe) as categoryLabel">Catégorie: {{ categoryLabel }}</span>
            <span *ngIf="formatCountryCodes(recipe) as countryCodes">Pays: {{ countryCodes }}</span>
          </div>

          <div *ngIf="recipe.ingredients?.length">
            <h3>Ingrédients</h3>
            <ul>
              <li *ngFor="let ingredient of recipe.ingredients">
                <strong *ngIf="ingredient.title">{{ ingredient.title }}:</strong>
                <span>{{ ingredient.content || '-' }}</span>
              </li>
            </ul>
          </div>

          <div *ngIf="recipe.steps?.length">
            <h3>Préparation</h3>
            <ol>
              <li *ngFor="let step of sortedRecipeSteps(recipe)">
                <strong *ngIf="step.title">{{ step.title }}:</strong>
                <span>{{ step.content || '-' }}</span>
              </li>
            </ol>
          </div>
        </ng-container>

        <ng-template #noDetails>
          <p class="details-empty">Aucun détail additionnel disponible pour cette recette.</p>
        </ng-template>
      </section>
    </article>
  </div>

  <div class="state" *ngIf="!loading && activeTab === 'inspirations' && inspirations.length === 0">
    Aucune inspiration à modérer.
  </div>

  <div class="grid" *ngIf="!loading && activeTab === 'inspirations' && inspirations.length > 0">
    <article *ngFor="let inspiration of inspirations">
      <h2>{{ inspiration.title || ('Inspiration #' + inspiration.id) }}</h2>
      <p>{{ inspiration.description || 'Sans description' }}</p>
      <div class="meta">
        <span>Recipe #{{ inspiration.recipeId }}</span>
        <span>Owner #{{ inspiration.ownerId }}</span>
        <span>{{ inspiration.status || 'REVIEW' }}</span>
      </div>
      <div class="meta">
        <span>♥ {{ inspiration.likes }}</span>
        <span>👁 {{ inspiration.views }}</span>
        <span>↗ {{ inspiration.shares }}</span>
      </div>
      <div class="actions">
        <button
          class="ok"
          [disabled]="isActionPending('inspiration:' + inspiration.id)"
          (click)="decideInspiration(inspiration, 'APPROVED')"
        >
          Approuver
        </button>
        <button
          class="ko"
          [disabled]="isActionPending('inspiration:' + inspiration.id)"
          (click)="decideInspiration(inspiration, 'DENIED')"
        >
          Rejeter
        </button>
      </div>
    </article>
  </div>

  <div class="state" *ngIf="!loading && activeTab === 'reviews' && reviews.length === 0">
    Aucun message d’avis à modérer.
  </div>

  <section class="reviews-panel" *ngIf="!loading && activeTab === 'reviews' && reviews.length > 0">
    <article class="review-card" *ngFor="let review of reviews">
      <div class="review-head">
        <div class="review-copy">
          <p class="review-kicker">Avis #{{ review.id }}</p>
          <h2>{{ review.authorDisplayName || ('Auteur #' + review.authorId) }}</h2>

          <div class="review-meta">
            <span>Recette #{{ review.recipeId || '-' }}</span>
            <span>Note {{ review.rating ?? '-' }}/5</span>
            <span *ngIf="review.createdAt">{{ review.createdAt | date:'dd/MM/yyyy HH:mm' }}</span>
          </div>
        </div>

        <span
          class="status-badge"
          [class.status-review]="(review.messageStatus || 'REVIEW') === 'REVIEW'"
          [class.status-approved]="review.messageStatus === 'APPROVED'"
          [class.status-denied]="review.messageStatus === 'DENIED'"
        >
          {{ review.messageStatus || 'REVIEW' }}
        </span>
      </div>

      <p class="review-message">{{ review.message || 'Sans message.' }}</p>

      <div class="actions review-actions">
        <button
          class="ok"
          [disabled]="isActionPending('review:' + review.id)"
          (click)="decideReview(review, 'APPROVED')"
        >
          Approuver
        </button>
        <button
          class="ko"
          [disabled]="isActionPending('review:' + review.id)"
          (click)="decideReview(review, 'DENIED')"
        >
          Rejeter
        </button>
      </div>
    </article>
  </section>

  <footer class="pagination" *ngIf="!loading">
    <span>{{ totalItems }} résultat(s)</span>
    <div>
      <button type="button" class="ghost" (click)="previousPage()" [disabled]="currentPage === 0">Préc.</button>
      <span>Page {{ currentPage + 1 }} / {{ totalPages }}</span>
      <button type="button" class="ghost" (click)="nextPage()" [disabled]="currentPage + 1 >= totalPages">Suiv.</button>
    </div>
  </footer>

  <div class="reject-modal-backdrop" *ngIf="rejectingRecipe">
    <section class="reject-modal" role="dialog" aria-modal="true" aria-label="Motif du rejet">
      <header>
        <h2>Motif du rejet</h2>
        <p>{{ rejectingRecipe.name }}</p>
      </header>

      <label for="reject-reason">Expliquez la raison (obligatoire)</label>
      <textarea
        id="reject-reason"
        rows="4"
        [value]="rejectionReasonDraft"
        [disabled]="isRejectSubmitting()"
        maxlength="500"
        (input)="rejectionReasonDraft = (($any($event.target).value || '').toString())"
        placeholder="Ex: ingrédients incohérents, photo non conforme, contenu dupliqué..."
      ></textarea>
      <small>{{ rejectionReasonDraft.length }}/500</small>
      <p class="reject-loading" *ngIf="isRejectSubmitting()">
        <span class="loader-dot" aria-hidden="true"></span>
        Envoi du motif en cours…
      </p>

      <footer>
        <button
          type="button"
          class="ghost"
          [disabled]="isRejectSubmitting()"
          (click)="closeRejectDialog()"
        >
          Annuler
        </button>
        <button
          type="button"
          class="ko"
          [disabled]="isRejectSubmitting()"
          (click)="confirmRejectDialog()"
        >
          <span class="btn-loading" *ngIf="isRejectSubmitting()">
            <span class="loader-dot" aria-hidden="true"></span>
            Envoi…
          </span>
          <span *ngIf="!isRejectSubmitting()">Confirmer le rejet</span>
        </button>
      </footer>
    </section>
  </div>
</section>
